import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    AllowanceEntity,
    BokkzAlgDataService,
    BokkzTslDataService,
    BokkzTslLogic,
    ClientEntity,
    Medebewoner,
    Periode,
    Woning
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { filter, Subject, takeUntil } from 'rxjs';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-huurtoeslag',
    templateUrl: './huurtoeslag.component.html',
    styleUrls: ['./huurtoeslag.component.scss'],
    imports: [
        SharedUiButtonModule,
        SharedUiDialogModule,
        ReactiveFormsModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        CommonModule
    ]
})
export class HuurtoeslagComponent implements OnInit, OnDestroy {

    @Input() public id!: string;
    private destroy$ = new Subject<void>();
    private clientEntity = {} as ClientEntity;
    private isSaving = false;
    private focusedElementId: string | null = null;
    public allowanceEntity = {} as AllowanceEntity;
    public huurToeslagFg = {} as FormGroup;
    public dialogVisible = false;
    public dialogMedebewonerVisible = false;
    public index = 0;
    public formStructure: any;
    public woningLength: any;
    public year: any;
    public heeftToeslagPartner = false;

    constructor(
        private router: Router,
        public tslLogic: BokkzTslLogic,
        public dataService: BokkzTslDataService,
        public dataAlgService: BokkzAlgDataService,
        private fb: FormBuilder) {
        this.huurToeslagFg = this.fb.group({
            alOntvangenHuurtoeslag: null as unknown as number,
            huurwoningen: this.fb.array([])
        });
    }

    showDialog(index: number) {
        this.index = index;
        this.dialogVisible = true;
    }

    showDialogMedebewoner(index: number) {
        this.index = index;
        this.dialogMedebewonerVisible = true;
    }

    get woningen(): FormArray {
        return this.huurToeslagFg.get('huurwoningen') as FormArray;
    }

    getMedebewoners(index: number): FormArray {
        const outerGroup = this.woningen.at(index) as FormGroup;
        return outerGroup.get('medebewoners') as FormArray;
    }

    addRij(): void {
        const huurWoning = this.createFormGroup();
        this.woningen.push(huurWoning);
    }

    verwijderRij(index: number): void {
        this.woningen.removeAt(index);
        this.huurToeslagFg.updateValueAndValidity();
        this.huurToeslagFg.markAsDirty();
        this.saveForm();
    }

    addMedebewoner(index: number): void {
        const innerGroup = this.createFormGroupMedebewoner();
        const outerGroup = this.woningen.at(index) as FormGroup;
        const innerArray = outerGroup.get('medebewoners') as FormArray;
        innerArray.push(innerGroup);
    }

    verwijderMedebewoner(outerIndex: number, innerIndex: number): void {
        const innerArray = this.getMedebewoners(outerIndex);
        innerArray.removeAt(innerIndex);
        this.huurToeslagFg.updateValueAndValidity();
        this.huurToeslagFg.markAsDirty();
        this.saveForm();
    }

    createFormGroup(ew?: Woning) {
        return this.fb.group({
            bewoondePeriode: this.fb.group<Periode>({
                begindatum: ew?.bewoondePeriode?.begindatum ? new Date(ew.bewoondePeriode.begindatum) : undefined,
                einddatum: ew?.bewoondePeriode?.einddatum ? new Date(ew.bewoondePeriode.einddatum) : undefined
            }),
            huisnummer: ew?.huisnummer || undefined,
            huisnummerToevoeging: ew?.huisnummerToevoeging || undefined,
            postcode: ew?.postcode || undefined,
            woonplaats: ew?.woonplaats || undefined,
            kaleHuurprijsPerMaand: ew?.kaleHuurprijsPerMaand || undefined,
            energiekostenPerMaand: ew?.energiekostenPerMaand || undefined,
            huismeersterkostenPerMaand: ew?.huismeersterkostenPerMaand || undefined,
            algemeneservicePerMaand: ew?.algemeneservicePerMaand || undefined,
            schoonmaakkostenPerMaand: ew?.schoonmaakkostenPerMaand || undefined,
            kreegHuursubsidieHoogVermogen: ew?.kreegHuursubsidieHoogVermogen || false,
            uitzonderingVoorRekenhuurVanToepassing: ew?.uitzonderingVoorRekenhuurVanToepassing || false,
            medebewoners: this.fb.array([]),
            aangepasteWoning: ew?.aangepasteWoning || false
        });
    }

    createFormGroupMedebewoner(medebewoner?: Medebewoner) {
        return this.fb.group({
            naam: medebewoner ? medebewoner.naam : null as unknown as string,
            geboortedatum: medebewoner ? new Date(medebewoner.geboortedatum) : undefined,
            bsn: medebewoner ? medebewoner.bsn : null as unknown as string,
            inkomen: medebewoner ? medebewoner.inkomen : null as unknown as number,
            toetsvermogen: medebewoner ? medebewoner.toetsvermogen : null as unknown as number,
            periodeSamenwonend: this.fb.group<Periode>({
                begindatum: medebewoner?.periodeSamenwonend?.begindatum ? new Date(medebewoner.periodeSamenwonend.begindatum) : undefined,
                einddatum: medebewoner?.periodeSamenwonend?.einddatum ? new Date(medebewoner.periodeSamenwonend.einddatum) : undefined
            })
        });
    }

    public ngOnInit(): void {
        this.dataService.allowanceEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.allowanceEntity = value;
            this.year = value.year;
            this.updateForm();
            this.heeftPartner();
        });
        this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((clientEntity) => {
            this.clientEntity = clientEntity;
        });
        this.huurToeslagFg.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            this.formStructure = value;
        });
        this.woningen.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
            this.woningLength = value;
        });
        this.router.events.pipe(
            takeUntil(this.destroy$),
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(() => {
                this.handleUrlChange();
            });
    }

    private updateForm(): void {
        const lijst = this.allowanceEntity?.toeslagen?.huurtoeslag?.huurwoningen || [];
        const formArray = this.huurToeslagFg.get('huurwoningen') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        // Update woningen
        for (let i = 0; i < lijst.length; i++) {
            const woning = lijst[i];

            // Formatteer de postcode voordat deze wordt gepatcht
            if (woning.postcode) {
                woning.postcode = this.formatPostcode(woning.postcode);
            }
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i] ?? {}); // Ensures control gets empty object if lijst[i] is undefined
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup);
            }
        }

        //update medebewoners
        for (let i = 0; i < lijst.length; i++) {
            const woning = lijst[i];
            const medebewoner = woning.medebewoners || [];
            const medebewonerFormArray = this.getMedebewoners(i);

            while (medebewonerFormArray.length > medebewoner.length) {
                medebewonerFormArray.removeAt(medebewonerFormArray.length - 1);
            }
            for (let j = 0; j < medebewoner.length; j++) {
                if (j < medebewonerFormArray.length) {
                    const control = medebewonerFormArray.at(j) as FormGroup;
                    control.patchValue(medebewoner[j] ?? {});
                } else {
                    const periode = this.createFormGroupMedebewoner(medebewoner[j]);
                    medebewonerFormArray.push(periode);
                }
            }
        }

        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        setTimeout(() => {
            this.huurToeslagFg.updateValueAndValidity();
            this.allowanceEntity.toeslagen = {
                ...this.allowanceEntity.toeslagen,
                huurtoeslag: {
                    ...this.allowanceEntity.toeslagen?.huurtoeslag,
                    huurwoningen: this.huurToeslagFg.value.huurwoningen
                }
            } as AllowanceEntity;
            this.dataService.updateAllowanceTaxEntity(this.allowanceEntity);
        }, 0);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleUrlChange() {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.tslLogic.postAllowances(this.clientEntity.sstRelationId!, this.allowanceEntity, false);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.huurToeslagFg.dirty;
    }

    formatPostcode(postcode: string | null | undefined): string {
        if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

        // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
        const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

        // Zorg voor 4 cijfers en 2 hoofdletters
        if (cleanedPostcode.length >= 4) {
            const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
            const letters = cleanedPostcode.substring(4, 6).replace(/[^a-zA-Z]/g, '').toUpperCase(); // Alleen letters
            return `${cijfers}${letters}`;
        }

        return cleanedPostcode; // Als de postcode te kort is, retourneer de schoongemaakte versie
    }

    private heeftPartner() {
        this.heeftToeslagPartner = false
        if (this.allowanceEntity.toeslagen && this.allowanceEntity.toeslagen?.inkomensgegevens?.at(0)?.bsnPartner != null) {
            this.heeftToeslagPartner = true
        }
    }
}
